import { translations, SupportedLanguageCode, fallback_language, normalizeLanguageCode } from './translations';
import { toolLabels } from './toolLabels';
import { storageService } from '../services/storageService';

type TranslationPath = string; // e.g., 'settings.title' or 'header.rewards'

const getFromObject = (obj: Record<string, any>, path: TranslationPath): string | undefined => {
  const parts = path.split('.');
  let current: any = obj;
  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return undefined;
    }
  }
  return typeof current === 'string' ? current : undefined;
};

class InternalI18n {
  private _language: SupportedLanguageCode;
  private _listeners: Array<() => void> = [];

  constructor() {
    const saved = storageService.getLanguage();
    const fromNavigator = typeof navigator !== 'undefined' ? navigator.language : fallback_language;
    const initial = normalizeLanguageCode(saved || fromNavigator || fallback_language);
    this._language = initial;
  }

  get language(): SupportedLanguageCode {
    return this._language;
  }

  changeLanguage(lang: string): void {
    const normalized = normalizeLanguageCode(lang);
    this._language = normalized;
    storageService.setLanguage(normalized);
    this._listeners.forEach(l => l());
  }

  t(path: TranslationPath): string {
    // Special-case dynamic tool labels: tools.<key>
    if (path.startsWith('tools.')) {
      const key = path.substring('tools.'.length);
      const byLangTools = toolLabels[this._language] || toolLabels[fallback_language];
      return byLangTools[key] || key;
    }
    const byLang = translations[this._language] || translations[fallback_language];
    const fallback = translations[fallback_language];
    return (
      getFromObject(byLang, path) ||
      getFromObject(fallback, path) ||
      path
    );
  }

  subscribe(listener: () => void): () => void {
    this._listeners.push(listener);
    return () => {
      this._listeners = this._listeners.filter(l => l !== listener);
    };
  }
}

export const i18n = new InternalI18n();

export const useTranslate = () => {
  // Lightweight hook that forces re-render when language changes
  // Avoid importing React here to keep this module framework-agnostic
  return i18n.t.bind(i18n);
};


